import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Virtuoso } from 'react-virtuoso';

import { channel as $channelContent } from '../../../../content';
import { clsm } from '../../../../utils';
import { useUser } from '../../../../contexts/User';
import { useChat } from '../../../../contexts/Chat';
import { useResponsiveDevice } from '../../../../contexts/ResponsiveDevice';
import ChatLine from './ChatLine';
import StickScrollButton from './StickScrollButton';
import useResize from '../../../../hooks/useResize';
// import { dummyMessages } from '../../../../constants/dummyMessages';
const $content = $channelContent.chat;

const Messages = ({
  isChatPopupOpen = false,
  isModerator = false,
  openChatPopup
}) => {
  const { userData } = useUser();
  const { sub: chatUserId } = userData || {};
  const chatRoomOwnerUsername = chatUserId;
  const chatRef = useRef();
  const virtuosoRef = useRef();
  const { messages: chatMessages, initMessages } = useChat();
  const [hasInitMessages, setHasInitMessages] = useState(false);
  const [isSticky, setIsSticky] = useState(true);
  const { isMobileView, isLandscape } = useResponsiveDevice();
  const isSplitView = isMobileView && isLandscape;

  // Memoize messages data for better performance
  const messages = useMemo(() => {
    return chatMessages?.length > 0 ? chatMessages : []; // change [] to dummyMessages for tesing purposes
  }, [chatMessages]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: messages.length - 1,
        behavior: 'smooth'
      });
    }
  }, [messages.length]);

  // Handle scroll state changes
  const handleAtBottomStateChange = useCallback((atBottom) => {
    setIsSticky(atBottom);
  }, []);

  // Render each message item
  const ItemContent = useCallback(
    (index, messageData) => {
      // Safety check to prevent accessing undefined message
      if (!messageData) {
        return (
          <div className="pb-3" style={{ minHeight: '40px' }}>
            <div className="text-gray-400 text-sm">Loading...</div>
          </div>
        );
      }

      const {
        content: message,
        id: messageId,
        isDeleted,
        isOwnMessage,
        isPreloaded,
        wasDeletedByUser,
        attributes: senderAttributes
      } = messageData;

      const avatarSrc = '';
      const selectMessageToModerate = () =>
        openChatPopup({
          id: messageId,
          message,
          avatarSrc,
          isOwnMessage,
          ...senderAttributes
        });

      if (isDeleted && (isOwnMessage || wasDeletedByUser))
        return (
          <div className="pb-3" style={{ minHeight: '40px' }}>
            <p
              data-testid="chatline-message-removed"
              className={clsm([
                'dark:text-darkMode-gray-light',
                'text-lightMode-gray-dark',
                'font-bold',
                'py-3'
              ])}
            >
              {$content.message_removed}
            </p>
          </div>
        );
      else if (isDeleted) {
        return (
          <div className="pb-3" style={{ minHeight: '20px' }}>
            {/* Hidden deleted message - maintains layout spacing */}
          </div>
        );
      }

      return (
        <div className="pb-3" style={{ minHeight: '40px' }}>
          <ChatLine
            {...(isModerator ? { onClick: selectMessageToModerate } : {})}
            {...senderAttributes}
            avatarSrc={avatarSrc}
            isFocusable={isModerator && !isChatPopupOpen}
            message={message}
            shouldAnimateIn={!isPreloaded}
          />
        </div>
      );
    },
    [isModerator, isChatPopupOpen, openChatPopup]
  );
  useEffect(() => {
    // Initialize messages - either from chat or use dummy messages
    if (chatRoomOwnerUsername || messages.length > 0) {
      if (chatRoomOwnerUsername) {
        initMessages();
      }
      setHasInitMessages(true);
    }

    return () => setHasInitMessages(false);
  }, [chatRoomOwnerUsername, initMessages, messages.length]);

  useEffect(() => {
    // Reset the sticky scroll when entering/exiting split view
    // as the scroll position will change between layouts
    setTimeout(scrollToBottom, 10);
  }, [isSplitView, scrollToBottom]);

  useResize(() => {
    // Reset the sticky scroll when resizing browser
    setTimeout(scrollToBottom, 10);
  });

  return (
    <div
      className={clsm([
        'flex',
        'h-full',
        'items-end',
        'justify-center',
        'relative',
        'w-full'
      ])}
    >
      <div
        className={clsm([
          'absolute',
          'h-full',
          'w-full',
          'px-[18px]',
          'pt-5',
          'z-30'
        ])}
        ref={chatRef}
      >
        {hasInitMessages ? (
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '100%' }}
            data={messages}
            itemContent={ItemContent}
            followOutput="smooth"
            atBottomStateChange={handleAtBottomStateChange}
            overscan={200}
            increaseViewportBy={200}
            initialTopMostItemIndex={Math.max(0, messages.length - 50)}
          />
        ) : null}
      </div>
      <StickScrollButton isSticky={isSticky} scrollToBottom={scrollToBottom} />
    </div>
  );
};

Messages.propTypes = {
  isChatPopupOpen: PropTypes.bool,
  isModerator: PropTypes.bool,
  openChatPopup: PropTypes.func.isRequired
};

export default Messages;
